<template>
  <div
    :class="[
      'automations-card',
      `automations-card--${variant}`,
      { 'automations-card--expanded': expanded },
    ]"
    @click="handleClick"
  >
    <div class="automations-card__inner">
      <!-- Header content -->
      <div :class="['automations-card__content', { 'automations-card__content--bordered': expanded }]">
        <!-- Title row -->
        <div class="automations-card__title-row">
          <div class="automations-card__title-icon">
            <!-- Error: red warning triangle -->
            <svg v-if="variant === 'error'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 15.75H16.5L9 2.25L1.5 15.75ZM9.75 13.5H8.25V12H9.75V13.5ZM9.75 10.5H8.25V7.5H9.75V10.5Z" fill="#D10000"/>
            </svg>
            <!-- Warning: orange circle exclamation -->
            <svg v-else-if="variant === 'warning'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9.75 13.5H8.25V12H9.75V13.5ZM9.75 10.5H8.25V4.5H9.75V10.5Z" fill="#FEB52E"/>
            </svg>
            <!-- Complete: green checkmark with circle -->
            <svg v-else width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.5 3.885L7.9425 12.45L4.7625 9.27L5.82 8.2125L7.9425 10.335L15.4425 2.835L16.5 3.885ZM14.8425 7.665C14.94 8.0925 15 8.5425 15 9C15 12.315 12.315 15 9 15C5.685 15 3 12.315 3 9C3 5.685 5.685 3 9 3C10.185 3 11.28 3.345 12.21 3.9375L13.29 2.8575C12.075 2.0025 10.5975 1.5 9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 8.1075 16.335 7.2525 16.05 6.4575L14.8425 7.665Z" fill="#588B8B"/>
            </svg>
            <span :class="['automations-card__title-text', { 'automations-card__title-text--muted': variant === 'complete' }]">
              {{ item.title }}
            </span>
          </div>
          <span :class="['automations-card__time', { 'automations-card__time--dark': variant === 'complete' }]">
            {{ item.timeAgo }}
          </span>
          <!-- Archive icon with tooltip (only for active items) -->
          <div v-if="variant !== 'complete'" class="automations-card__archive-wrap" @click.stop="$emit('archive', item.id)">
            <svg class="automations-card__archive-icon" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5 0H1.5C0.75 0 0 0.675 0 1.5V3.7575C0 4.2975 0.3225 4.7625 0.75 5.025V13.5C0.75 14.325 1.575 15 2.25 15H12.75C13.425 15 14.25 14.325 14.25 13.5V5.025C14.6775 4.7625 15 4.2975 15 3.7575V1.5C15 0.675 14.25 0 13.5 0ZM12.75 13.5H2.25V5.25H12.75V13.5ZM13.5 3.75H1.5V1.5H13.5V3.75Z" fill="#374957"/>
              <path d="M9.75 7.5H5.25V9H9.75V7.5Z" fill="#374957"/>
            </svg>
            <div class="automations-card__archive-tooltip">Archive</div>
          </div>
        </div>
        <!-- Descriptions -->
        <div class="automations-card__descriptions">
          <div :class="['automations-card__patient', { 'automations-card__patient--muted': variant === 'complete' }]">
            <span class="automations-card__patient-label">Patient:</span>
            <span class="automations-card__patient-name">{{ item.patientName }}</span>
          </div>
          <p :class="['automations-card__description', { 'automations-card__description--muted': variant === 'complete' }]" v-html="renderDescriptionPreview(item.description)">
          </p>
        </div>
      </div>

      <!-- Expandable content with slide animation -->
      <transition
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
        @after-leave="afterLeave"
      >
        <div v-if="expanded" class="automations-card__expandable">
          <!-- Loading messages -->
          <div v-if="!item.chatHistory && messagesLoading" class="automations-card__history-loading" @click.stop>
            <span>Loading messages...</span>
          </div>
          <!-- Chat history -->
          <div v-else-if="item.chatHistory" ref="chatHistory" class="automations-card__history" @click.stop>
            <div
              v-for="(msg, idx) in item.chatHistory"
              :key="idx"
              class="automations-card__chat-item"
            >
              <!-- Robot message -->
              <template v-if="msg.type === 'robot'">
                <div class="automations-card__chat-icon">
                  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M16 2.66666C16.5862 2.66769 17.1556 2.86183 17.6204 3.21904C18.0851 3.57624 18.4192 4.07662 18.571 4.64278C18.7228 5.20894 18.6838 5.80934 18.4601 6.35112C18.2364 6.8929 17.8404 7.34588 17.3333 7.63999V7.99999H21.3333C22.7478 7.99999 24.1044 8.56189 25.1046 9.56209C26.1048 10.5623 26.6667 11.9188 26.6667 13.3333V13.4C27.4191 13.5543 28.0952 13.9637 28.5807 14.5588C29.0662 15.154 29.3313 15.8986 29.3313 16.6667C29.3313 17.4347 29.0662 18.1793 28.5807 18.7745C28.0952 19.3697 27.4191 19.779 26.6667 19.9333V21.3333C26.6667 22.7478 26.1048 24.1044 25.1046 25.1046C24.1044 26.1048 22.7478 26.6667 21.3333 26.6667H10.6667C9.25218 26.6667 7.89563 26.1048 6.89543 25.1046C5.89524 24.1044 5.33333 22.7478 5.33333 21.3333V19.9333C4.57981 19.7803 3.90235 19.3715 3.41575 18.7762C2.92915 18.1808 2.66333 17.4356 2.66333 16.6667C2.66333 15.8978 2.92915 15.1525 3.41575 14.5572C3.90235 13.9618 4.57981 13.553 5.33333 13.4V13.3333C5.33333 11.9188 5.89524 10.5623 6.89543 9.56209C7.89563 8.56189 9.25218 7.99999 10.6667 7.99999H14.6667V7.63999C14.1596 7.34588 13.7636 6.8929 13.5399 6.35112C13.3162 5.80934 13.2772 5.20894 13.429 4.64278C13.5808 4.07662 13.9149 3.57624 14.3796 3.21904C14.8444 2.86183 15.4138 2.66769 16 2.66666ZM12 14.6667C11.6464 14.6667 11.3072 14.8071 11.0572 15.0572C10.8071 15.3072 10.6667 15.6464 10.6667 16V18.6667C10.6667 19.0203 10.8071 19.3594 11.0572 19.6095C11.3072 19.8595 11.6464 20 12 20C12.3536 20 12.6928 19.8595 12.9428 19.6095C13.1929 19.3594 13.3333 19.0203 13.3333 18.6667V16C13.3333 15.6464 13.1929 15.3072 12.9428 15.0572C12.6928 14.8071 12.3536 14.6667 12 14.6667ZM20 14.6667C19.6464 14.6667 19.3072 14.8071 19.0572 15.0572C18.8071 15.3072 18.6667 15.6464 18.6667 16V18.6667C18.6667 19.0203 18.8071 19.3594 19.0572 19.6095C19.3072 19.8595 19.6464 20 20 20C20.3536 20 20.6928 19.8595 20.9428 19.6095C21.1929 19.3594 21.3333 19.0203 21.3333 18.6667V16C21.3333 15.6464 21.1929 15.3072 20.9428 15.0572C20.6928 14.8071 20.3536 14.6667 20 14.6667Z" fill="#5D5FEF"/>
                  </svg>
                </div>
                <div class="automations-card__bubble-wrap">
                  <div class="automations-card__bubble-pointer"></div>
                  <div class="automations-card__bubble automations-card__bubble--robot">
                    <template v-if="Array.isArray(msg.text)">
                      <div v-for="(line, li) in msg.text" :key="li" class="automations-card__bubble-text automations-card__markdown" v-html="renderMarkdown(line)" />
                    </template>
                    <div
                      v-else
                      class="automations-card__bubble-text automations-card__markdown"
                      v-html="isStreamingMsg(idx) ? streamingHtml : renderMarkdown(msg.text)"
                    />
                  </div>
                </div>
              </template>

              <!-- Error message -->
              <template v-else-if="msg.type === 'error'">
                <div class="automations-card__chat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 15H13V17H11V15ZM11 7H13V13H11V7ZM11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z" fill="#D10000"/>
                  </svg>
                </div>
                <div class="automations-card__bubble automations-card__bubble--error">
                  <p class="automations-card__bubble-text automations-card__bubble-text--error">{{ msg.text }}</p>
                </div>
              </template>

              <!-- Warning message -->
              <template v-else-if="msg.type === 'warning'">
                <div class="automations-card__chat-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 15H13V17H11V15ZM11 7H13V13H11V7ZM11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z" fill="#DD9000"/>
                  </svg>
                </div>
                <div class="automations-card__bubble automations-card__bubble--warning">
                  <p class="automations-card__bubble-text automations-card__bubble-text--warning">{{ msg.text }}</p>
                </div>
              </template>

              <!-- Provider message -->
              <template v-else-if="msg.type === 'provider'">
                <div class="automations-card__chat-item--provider">
                  <div class="automations-card__bubble-wrap automations-card__bubble-wrap--provider">
                    <div class="automations-card__bubble automations-card__bubble--provider">
                      <p class="automations-card__bubble-text">{{ msg.text }}</p>
                    </div>
                    <div class="automations-card__bubble-pointer automations-card__bubble-pointer--right"></div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Send message bar -->
          <div class="automations-card__send-bar">
            <input
              v-model="replyText"
              class="automations-card__send-input"
              type="text"
              placeholder="Reply to agent..."
              @click.stop
              @keyup.enter="sendMessage"
            />
            <button class="automations-card__send-btn" :disabled="sendingMessage" @click.stop="sendMessage">
              {{ sendingMessage ? 'Sending...' : 'Send' }}
            </button>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked';

export default {
  name: 'AutomationsCard',
  props: {
    item: { type: Object, required: true },
    variant: { type: String, default: 'error', validator: v => ['error', 'warning', 'complete'].includes(v) },
    expanded: { type: Boolean, default: false },
    messagesLoading: { type: Boolean, default: false },
    sendingMessage: { type: Boolean, default: false },
  },
  emits: ['toggle-expand', 'send-message', 'archive'],
  data() {
    return {
      replyText: '',
      streamingIndex: -1,
      streamingText: '',
      streamingHtml: '',
      streamingTimer: null,
    };
  },
  watch: {
    'item.chatHistory': {
      deep: true,
      handler(newVal, oldVal) {
        this.$nextTick(() => {
          const el = this.$refs.chatHistory;
          if (el) {
            el.scrollTop = el.scrollHeight;
          }
        });
        // Start streaming when a new robot message is appended
        if (newVal && oldVal && newVal.length > oldVal.length) {
          const lastIdx = newVal.length - 1;
          const lastMsg = newVal[lastIdx];
          if (lastMsg && lastMsg.type === 'robot' && !Array.isArray(lastMsg.text)) {
            this.startStreaming(lastIdx, lastMsg.text);
          }
        }
      },
    },
  },
  beforeUnmount() {
    this.clearStreamingTimer();
  },
  methods: {
    renderDescriptionPreview(text) {
      if (!text) return '';
      return text
        .replace(/\\n/g, ' ')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, ' ')
        .replace(/\*\*/g, '')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },
    renderMarkdown(text) {
      if (!text) return '';
      // Unescape literal escape sequences from backend (e.g. \\n, \\\", \\t)
      const normalized = text
        .replace(/\\n/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\t/g, '\t');
      return marked.parse(normalized, { breaks: true });
    },
    isStreamingMsg(idx) {
      return this.streamingIndex === idx;
    },
    startStreaming(idx, fullText) {
      this.clearStreamingTimer();
      this.streamingIndex = idx;
      this.streamingText = '';
      this.streamingHtml = '';
      let charIdx = 0;
      const step = () => {
        if (charIdx >= fullText.length) {
          this.streamingIndex = -1;
          this.streamingText = '';
          this.streamingHtml = '';
          this.streamingTimer = null;
          return;
        }
        charIdx++;
        this.streamingText = fullText.slice(0, charIdx);
        this.streamingHtml = this.renderMarkdown(this.streamingText);
        this.$nextTick(() => {
          const el = this.$refs.chatHistory;
          if (el) el.scrollTop = el.scrollHeight;
        });
        this.streamingTimer = setTimeout(step, 15);
      };
      step();
    },
    clearStreamingTimer() {
      if (this.streamingTimer) {
        clearTimeout(this.streamingTimer);
        this.streamingTimer = null;
      }
    },
    handleClick() {
      if (this.variant !== 'complete') {
        this.$emit('toggle-expand', this.item.id);
      }
    },
    sendMessage() {
      if (!this.replyText.trim()) return;
      this.$emit('send-message', { id: this.item.id, text: this.replyText.trim() });
      this.replyText = '';
    },
    /* Expand/collapse transition hooks */
    beforeEnter(el) {
      el.style.height = '0';
      el.style.opacity = '0';
      el.style.overflow = 'hidden';
    },
    enter(el, done) {
      // Force layout so the browser registers height: 0 first
      void el.offsetHeight;
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = el.scrollHeight + 'px';
      el.style.opacity = '1';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterEnter(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
    beforeLeave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflow = 'hidden';
    },
    leave(el, done) {
      void el.offsetHeight;
      el.style.transition = 'height 0.3s ease, opacity 0.3s ease';
      el.style.height = '0';
      el.style.opacity = '0';
      el.addEventListener('transitionend', done, { once: true });
    },
    afterLeave(el) {
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    },
  },
};
</script>

<style scoped>
.automations-card {
  border-left: 4px solid;
  border-radius: 0 10px 10px 0;
  min-height: 132px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
  overflow: hidden;
  display: flex;
}

.automations-card--complete {
  cursor: default;
}

.automations-card--error {
  border-left-color: #D10000;
  background: #fff;
}

.automations-card--warning {
  border-left-color: #FEB52E;
  background: #fff;
}

.automations-card--error:hover,
.automations-card--error.automations-card--expanded {
  background: #FFFBFB;
  box-shadow: 0px 4px 12px rgba(26, 32, 44, 0.06);
}

.automations-card--warning:hover,
.automations-card--warning.automations-card--expanded {
  background: #FFFCFA;
  box-shadow: 0px 4px 12px rgba(26, 32, 44, 0.06);
}

.automations-card--complete {
  border-left-color: #588B8B;
  background: #F9F9F9;
}

.automations-card__inner {
  border: 1px solid #E0E0E0;
  border-left: none;
  border-radius: 0 10px 10px 0;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
}

.automations-card__content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.automations-card__content--bordered {
  border-bottom: 1px dashed #C4C4C4;
  padding-bottom: 12px;
}

.automations-card__title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.automations-card__title-icon {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.automations-card__title-icon svg {
  flex-shrink: 0;
}

.automations-card__title-text {
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #374957;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.automations-card__title-text--muted {
  color: #999;
}

.automations-card__time {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  white-space: nowrap;
  flex-shrink: 0;
}

.automations-card__time--dark {
  color: #333;
  text-align: right;
}

.automations-card__archive-wrap {
  position: relative;
  flex-shrink: 0;
  cursor: pointer;
}

.automations-card__archive-icon {
  display: block;
}

.automations-card__archive-tooltip {
  display: none;
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #666;
  color: #fff;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
  white-space: nowrap;
  padding: 4px 12px;
  border-radius: 2px;
  box-shadow: 0px 4px 12px rgba(26, 32, 44, 0.06);
  z-index: 10;
}

.automations-card__archive-tooltip::before {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-bottom-color: #666;
}

.automations-card__archive-wrap:hover .automations-card__archive-tooltip {
  display: block;
}

.automations-card__descriptions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.automations-card__patient {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.4px;
}

.automations-card__patient-label {
  color: #999;
}

.automations-card__patient-name {
  color: #666;
}

.automations-card__patient--muted .automations-card__patient-label,
.automations-card__patient--muted .automations-card__patient-name {
  color: #999;
}

.automations-card__description {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.2px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  margin: 0;
}

.automations-card__description--muted {
  color: #999;
}

/* Expandable wrapper */
.automations-card__expandable {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Chat history */
.automations-card__history {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 221px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 transparent;
}

.automations-card__history::-webkit-scrollbar {
  width: 4px;
}

.automations-card__history::-webkit-scrollbar-track {
  background: transparent;
}

.automations-card__history::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.automations-card__chat-item {
  display: flex;
  align-items: center;
  gap: 2px;
}

.automations-card__chat-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Bubble wrap with pointer */
.automations-card__bubble-wrap {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.automations-card__bubble-pointer {
  width: 14px;
  height: 18px;
  flex-shrink: 0;
  background: #F2F2F2;
  clip-path: polygon(100% 0%, 0% 50%, 100% 100%);
  margin-right: -1px;
}

.automations-card__bubble-pointer--right {
  background: #E3F2FD;
  clip-path: polygon(0% 0%, 100% 50%, 0% 100%);
  margin-right: 0;
  margin-left: -1px;
}

.automations-card__bubble-wrap--provider {
  justify-content: flex-end;
}

/* Bubbles */
.automations-card__bubble {
  border-radius: 20px;
  padding: 12px 24px;
  flex: 1;
  min-width: 0;
}

.automations-card__bubble--robot {
  background: #F2F2F2;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.automations-card__bubble--error {
  background: #FAEAEA;
}

.automations-card__bubble--warning {
  background: #F7EECE;
}

.automations-card__chat-item--provider {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.automations-card__bubble--provider {
  background: #E3F2FD;
  padding: 12px 24px;
  flex: 0 1 auto;
  max-width: 80%;
}

.automations-card__history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  color: #999;
  font-family: Inter, sans-serif;
  font-size: 13px;
}

.automations-card__bubble-text {
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  margin: 0;
}

.automations-card__bubble-text--error {
  color: #D10000;
}

.automations-card__bubble-text--warning {
  color: #DD9000;
}

/* Markdown rendered content */
.automations-card__markdown :deep(p) {
  margin: 0 0 4px 0;
}

.automations-card__markdown :deep(p:last-child) {
  margin-bottom: 0;
}

.automations-card__markdown :deep(ol),
.automations-card__markdown :deep(ul) {
  margin: 4px 0;
  padding-left: 20px;
}

.automations-card__markdown :deep(li) {
  margin-bottom: 2px;
}

.automations-card__markdown :deep(strong) {
  font-weight: 600;
}

.automations-card__markdown :deep(code) {
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 13px;
}

/* Send message bar */
.automations-card__send-bar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.automations-card__send-input {
  flex: 1;
  min-width: 0;
  height: 44px;
  background: #fff;
  border: 1px solid #E0E0E0;
  border-radius: 20px;
  padding: 12px 24px;
  font-family: Inter, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #333;
  outline: none;
  box-sizing: border-box;
}

.automations-card__send-input::placeholder {
  color: #999;
}

.automations-card__send-btn {
  flex-shrink: 0;
  background: #004879;
  border: none;
  border-radius: 15px;
  padding: 4px 12px;
  box-shadow: 0px 1px 3px rgba(74, 74, 74, 0.25);
  font-family: Inter, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;
}

.automations-card__send-btn:hover {
  background: #003a61;
}

.automations-card__send-btn:disabled {
  background: #99b7cc;
  cursor: not-allowed;
}
</style>
